import { Button, Textarea } from "@design";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ToolConfirmationDecision } from "@/api";
import { useProposalContext } from "../research/ProposalContext";
import type { ActionProposal } from "./actionModel";

type Choice = "run" | "remember" | "decline";

/**
 * The owner's choice on a pending action (MHS-971): Approve, Approve for the rest of this chat (when the server named
 * where it goes), Edit the text first, or Don't do it. Sent once (a double click too); waits while a reply is coming.
 */
export function ActionChoice({
  action,
  editing,
  onEditing,
  rememberLabel,
}: Readonly<{
  action: ActionProposal;
  editing: boolean;
  onEditing: (editing: boolean) => void;
  /** What "remember" means here: the rest of this chat (default), or always in a workflow. */
  rememberLabel?: string;
}>) {
  const { t } = useTranslation("chat", { keyPrefix: "action" });
  const { busy, onDecide } = useProposalContext();
  const [drafts, setDrafts] = useState<Record<string, string>>(action.editable);
  const [deciding, setDeciding] = useState<Choice | null>(null);
  // Set before the send starts, so a second click while it's on its way decides nothing.
  const sent = useRef(false);
  const disabled = busy || deciding !== null;
  const canEdit = Object.keys(action.editable).length > 0;

  const decide = (choice: Choice) => {
    if (sent.current || !onDecide) return;
    sent.current = true;
    setDeciding(choice);
    const decision: ToolConfirmationDecision = { id: action.id, decision: choice === "decline" ? "decline" : "run" };
    if (choice !== "decline") {
      // Only what I changed: the rest runs as proposed.
      const changed = Object.fromEntries(
        Object.entries(drafts).filter(([key, value]) => value !== action.editable[key]),
      );
      if (Object.keys(changed).length > 0) decision.overrides = changed;
      if (choice === "remember") decision.remember = true;
    }
    const label = { run: t("approve"), remember: rememberLabel ?? t("approveForChat"), decline: t("decline") }[choice];
    onDecide(label, decision, () => {
      sent.current = false;
      setDeciding(null);
    });
  };

  return (
    <>
      {editing &&
        Object.keys(action.editable).map((field) => (
          <Textarea
            key={field}
            aria-label={t("editField", { field })}
            rows={field === "title" || field === "subject" ? 1 : 4}
            value={drafts[field] ?? ""}
            disabled={disabled}
            onChange={(event) => setDrafts((current) => ({ ...current, [field]: event.target.value }))}
          />
        ))}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          size="sm"
          loading={deciding === "run"}
          disabled={disabled}
          onClick={() => decide("run")}
        >
          {t("approve")}
        </Button>
        {action.rememberable && (
          <Button
            variant="secondary"
            size="sm"
            loading={deciding === "remember"}
            disabled={disabled}
            onClick={() => decide("remember")}
          >
            {rememberLabel ?? t("approveForChat")}
          </Button>
        )}
        {canEdit && !editing && (
          <Button variant="secondary" size="sm" disabled={disabled} onClick={() => onEditing(true)}>
            {t("edit")}
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          loading={deciding === "decline"}
          disabled={disabled}
          onClick={() => decide("decline")}
        >
          {t("decline")}
        </Button>
      </div>
    </>
  );
}
